import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { formatFileSize, DOCUMENT_CATEGORIES, ACCESS_LEVELS, getExpiryStatus } from '../../services/docuvaultService';

const {
  FiFileText, FiTrash2, FiDownload, FiShield, FiLock, FiUsers,
  FiCheck, FiEye, FiChevronDown, FiChevronUp, FiClock, FiKey,
  FiAlertTriangle, FiLoader, FiList, FiZap,
} = FiIcons;

const typeIcons = {
  'application/pdf': '📄',
  'image/jpeg': '🖼️', 'image/png': '🖼️', 'image/gif': '🖼️',
  'application/msword': '📝',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '📝',
  'application/vnd.ms-excel': '📊',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '📊',
  'text/plain': '📃',
};

const EXPIRY_BADGES = {
  expired:  { label: 'Expired',    className: 'bg-red-100 text-red-600' },
  critical: { label: 'Expiring ≤3d', className: 'bg-red-50 text-red-500' },
  warning:  { label: 'Expiring ≤14d', className: 'bg-amber-50 text-amber-600' },
  info:     { label: 'Expiring ≤30d', className: 'bg-blue-50 text-blue-500' },
};

const DocuVaultDocumentCard = ({ doc, onDelete, onAuthorize, onView, onAccessLog, onEmergencyRelease, index }) => {
  const [expanded,  setExpanded]  = useState(false);
  const [deleting,  setDeleting]  = useState(false);
  const [deleteErr, setDeleteErr] = useState('');

  const approvals = (() => { try { return JSON.parse(doc.authorization_approvals || '[]'); } catch { return []; } })();
  const parties   = (() => { try { return JSON.parse(doc.authorized_parties   || '[]'); } catch { return []; } })();
  const tags      = (() => { try { return JSON.parse(doc.tags                  || '[]'); } catch { return []; } })();

  const threshold         = doc.authorization_threshold || 1;
  const isFullyAuthorized = approvals.length >= threshold;
  const progress          = Math.min((approvals.length / threshold) * 100, 100);
  const cat               = DOCUMENT_CATEGORIES.find(c => c.id === doc.file_category) || DOCUMENT_CATEGORIES[DOCUMENT_CATEGORIES.length - 1];
  const access            = ACCESS_LEVELS.find(a => a.id === doc.access_level) || ACCESS_LEVELS[0];
  const fileEmoji         = typeIcons[doc.file_type] || '📁';
  const expiryStatus      = getExpiryStatus(doc);
  const expiryBadge       = EXPIRY_BADGES[expiryStatus];

  // ── Atomic delete: pass both id AND s3_key to the parent ─────────────────
  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    setDeleteErr('');
    try {
      await onDelete?.(doc.id, doc.s3_key);
    } catch (err) {
      setDeleteErr(err.message || 'Deletion failed. Please try again.');
      setDeleting(false);
    }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className={`bg-white rounded-[28px] border-2 overflow-hidden transition-all hover:shadow-lg ${
        expiryStatus === 'expired' || expiryStatus === 'critical'
          ? 'border-red-200'
          : expiryStatus === 'warning'
            ? 'border-amber-200'
            : isFullyAuthorized && doc.authorization_required
              ? 'border-green-200'
              : 'border-brand-dark hover:border-accent-navy/30'
      }`}
    >
      {/* Card Header */}
      <div className="p-5 flex items-start gap-4">
        <div className="text-3xl flex-shrink-0 mt-0.5">{fileEmoji}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="min-w-0">
              <p className="text-sm font-bold text-accent-navy truncate">{doc.original_name}</p>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <span className="text-[9px] font-black uppercase tracking-widest text-text-muted bg-brand-light px-2 py-0.5 rounded-full">
                  {cat.label}
                </span>
                <span className="text-[9px] text-text-muted">{formatFileSize(doc.file_size)}</span>
                <span className="text-[9px] text-text-muted">{new Date(doc.created_at).toLocaleDateString()}</span>
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {expiryBadge && (
                <span className={`text-[8px] px-2 py-1 rounded-lg font-black uppercase ${expiryBadge.className}`}>
                  {expiryBadge.label}
                </span>
              )}
              <div className="flex items-center gap-1 bg-green-50 border border-green-100 px-2 py-1 rounded-xl">
                <SafeIcon icon={FiLock} className="w-3 h-3 text-green-600" />
                <span className="text-[8px] font-black text-green-700 uppercase">AES-256</span>
              </div>
            </div>
          </div>

          {/* Access Level Badge */}
          <div className="flex items-center gap-2">
            <SafeIcon
              icon={doc.access_level === 'private' ? FiLock : FiUsers}
              className="w-3.5 h-3.5 text-text-muted"
            />
            <span className="text-[10px] text-text-muted font-medium">{access.label}</span>
          </div>

          {/* Multi-party authorization progress */}
          {doc.authorization_required === 1 && (
            <div className="mt-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[9px] font-black uppercase tracking-widest text-text-muted">Authorization</span>
                <span className={`text-[9px] font-black ${isFullyAuthorized ? 'text-green-600' : 'text-accent-gold'}`}>
                  {approvals.length}/{threshold} Approvals
                </span>
              </div>
              <div className="h-1.5 bg-brand-light rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${isFullyAuthorized ? 'bg-green-500' : 'bg-accent-gold'}`}
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Tags */}
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {tags.map(t => (
                <span key={t} className="text-[8px] px-2 py-0.5 bg-accent-navy/5 text-accent-navy rounded-full font-bold">#{t}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete error banner */}
      {deleteErr && (
        <div className="mx-5 mb-3 flex items-start gap-2 p-2.5 bg-red-50 border border-red-200 rounded-xl text-[10px] text-red-700">
          <SafeIcon icon={FiAlertTriangle} className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>{deleteErr}</span>
        </div>
      )}

      {/* Actions */}
      <div className="px-5 pb-4 flex items-center justify-between border-t border-brand-light pt-3">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => onView?.(doc)}
            className="flex items-center gap-1.5 px-3 py-2 bg-brand-light hover:bg-accent-navy/5 text-accent-navy rounded-xl text-[9px] font-black uppercase tracking-widest transition-all"
          >
            <SafeIcon icon={FiEye} className="w-3.5 h-3.5" /> View
          </button>
          <button
            onClick={() => onAuthorize?.(doc)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${
              doc.authorization_required === 1
                ? 'bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200'
                : 'bg-brand-light hover:bg-accent-navy/5 text-text-muted border border-brand-dark'
            }`}
          >
            <SafeIcon icon={FiKey} className="w-3.5 h-3.5" />
            {doc.authorization_required === 1 ? 'Authorize' : 'Access'}
          </button>
          <button
            onClick={() => onAccessLog?.(doc)}
            className="flex items-center gap-1.5 px-3 py-2 bg-brand-light hover:bg-accent-navy/5 text-text-muted hover:text-accent-navy rounded-xl text-[9px] font-black uppercase tracking-widest transition-all"
            title="Access log"
          >
            <SafeIcon icon={FiList} className="w-3.5 h-3.5" /> Log
          </button>
          <button
            onClick={() => onEmergencyRelease?.(doc)}
            className="flex items-center gap-1.5 px-3 py-2 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all"
            title="Request emergency release"
          >
            <SafeIcon icon={FiZap} className="w-3.5 h-3.5" /> Emergency
          </button>
        </div>

        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-2 text-text-muted hover:text-accent-navy rounded-xl border border-brand-dark hover:border-accent-navy transition-all"
          >
            <SafeIcon icon={expanded ? FiChevronUp : FiChevronDown} className="w-4 h-4" />
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className={`p-2 rounded-xl border transition-all ${
              deleting
                ? 'text-text-muted border-brand-dark cursor-not-allowed'
                : 'text-text-muted hover:text-red-500 border-brand-dark hover:border-red-200'
            }`}
            title={deleting ? 'Deleting…' : 'Delete document'}
          >
            <SafeIcon icon={deleting ? FiLoader : FiTrash2} className={`w-4 h-4 ${deleting ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          className="border-t border-brand-light px-5 py-4 bg-brand-light/40 space-y-3"
        >
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div>
              <p className="text-[9px] font-black uppercase tracking-widest text-text-muted mb-0.5">S3 Bucket</p>
              <p className="font-mono text-accent-navy text-[10px] truncate">{doc.s3_bucket}</p>
            </div>
            <div>
              <p className="text-[9px] font-black uppercase tracking-widest text-text-muted mb-0.5">Checksum (SHA-256)</p>
              <p className="font-mono text-accent-navy text-[10px] truncate">{doc.checksum?.slice(0, 20)}…</p>
            </div>
            <div>
              <p className="text-[9px] font-black uppercase tracking-widest text-text-muted mb-0.5">Version</p>
              <p className="font-bold text-accent-navy">v{doc.version}</p>
            </div>
            <div>
              <p className="text-[9px] font-black uppercase tracking-widest text-text-muted mb-0.5">Encryption</p>
              <div className="flex items-center gap-1">
                <SafeIcon icon={FiShield} className="w-3.5 h-3.5 text-green-500" />
                <p className="font-bold text-green-700 capitalize">{doc.encryption_status}</p>
              </div>
            </div>
          </div>

          {doc.description && (
            <div>
              <p className="text-[9px] font-black uppercase tracking-widest text-text-muted mb-1">Description</p>
              <p className="text-xs text-accent-navy font-light">{doc.description}</p>
            </div>
          )}

          {parties.length > 0 && (
            <div>
              <p className="text-[9px] font-black uppercase tracking-widest text-text-muted mb-1.5">Authorized Parties</p>
              <div className="space-y-1.5">
                {parties.map((p, i) => {
                  const approved = approvals.find(a => a.email?.toLowerCase() === p.email?.toLowerCase());
                  return (
                    <div key={i} className="flex items-center justify-between text-[10px]">
                      <span className="font-bold text-accent-navy">
                        {p.name} <span className="text-text-muted font-light">({p.role})</span>
                      </span>
                      {approved
                        ? <span className="flex items-center gap-1 text-green-600 font-black"><SafeIcon icon={FiCheck} className="w-3 h-3" /> Approved</span>
                        : <span className="text-text-muted font-light flex items-center gap-1"><SafeIcon icon={FiClock} className="w-3 h-3" /> Pending</span>
                      }
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {doc.expiry_date && (
            <div className={`flex items-center gap-2 text-[10px] ${
              expiryStatus === 'expired' ? 'text-red-600' :
              expiryStatus === 'critical' ? 'text-red-500' :
              expiryStatus === 'warning' ? 'text-amber-600' :
              'text-text-muted'
            }`}>
              <SafeIcon icon={expiryStatus === 'expired' || expiryStatus === 'critical' ? FiAlertTriangle : FiClock} className="w-3.5 h-3.5" />
              {expiryStatus === 'expired' ? 'Expired' : 'Expires'}: {new Date(doc.expiry_date).toLocaleDateString()}
            </div>
          )}
        </motion.div>
      )}
    </motion.div>
  );
};

export default DocuVaultDocumentCard;